import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import ParentLayout from '../components/ParentLayout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import CloverPaymentForm from '../components/CloverPaymentForm';
import { 
  CheckCircle2, Circle, ArrowRight, ArrowLeft, Check, 
  Flag, Shield, Sparkles, CreditCard, DollarSign, 
  Smartphone, AlertCircle, Clock, CheckCircle, Tag, Users, Upload, FileText, ExternalLink,
  Eye, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Parse "YYYY-MM-DD" (or full-ISO with 'T' suffix) as a LOCAL-time calendar
// date — never UTC — to avoid timezone-driven day shifting on display.
// `new Date("YYYY-MM-DD")` is parsed by the JS spec as UTC midnight, which
// renders as the previous calendar day in any timezone west of UTC.
const parseLocalDate = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

// Early bird deadline: May 10
const EARLY_BIRD_DEADLINE = new Date('2026-05-10T23:59:59');

// Division definitions with proper naming and logo URLs
// IDs match the canonical division_id values in MongoDB (db.divisions)
const DIVISIONS = {
  // Flag Football
  div_little_chiefs: {
    id: 'div_little_chiefs',
    programType: 'flag_football',
    name: 'Little Chiefs',
    ageRange: '5-7',
    displayName: 'Flag Football — Little Chiefs (5–7U)',
    logoUrl: 'https://customer-assets.emergentagent.com/job_tesa-pcc-build/artifacts/i13wvccu_APP_LOGO_Flag-Football_Little-Chiefs_5_7U.png'
  },
  // Tackle Football
  div_tiny_chiefs: {
    id: 'div_tiny_chiefs',
    programType: 'tackle_football',
    name: 'Tiny Chiefs',
    ageRange: '7-8',
    displayName: 'Tackle Football — Tiny Chiefs (7–8U)',
    logoUrl: 'https://customer-assets.emergentagent.com/job_tesa-pcc-build/artifacts/jnl559tx_APP_LOGO_Tackle-Football_Tiny-Chiefs_7_8U.png'
  },
  div_mighty_chiefs: {
    id: 'div_mighty_chiefs',
    programType: 'tackle_football',
    name: 'Mighty Chiefs',
    ageRange: '9-9',
    displayName: 'Tackle Football — Mighty Chiefs (9U)',
    logoUrl: 'https://customer-assets.emergentagent.com/job_tesa-pcc-build/artifacts/d46yj8ap_APP_LOGO_Tackle-Football_Mighty-Chiefs_9U.png'
  },
  div_junior_chiefs: {
    id: 'div_junior_chiefs',
    programType: 'tackle_football',
    name: 'Junior Chiefs',
    ageRange: '10-10',
    displayName: 'Tackle Football — Junior Chiefs (10U)',
    logoUrl: 'https://customer-assets.emergentagent.com/job_tesa-pcc-build/artifacts/g7uicsn8_APP_LOGO_Tackle-Football_Junior-Chiefs_10U.png'
  },
  div_varsity_chiefs: {
    id: 'div_varsity_chiefs',
    programType: 'tackle_football',
    name: 'Varsity Chiefs',
    ageRange: '11-11',
    displayName: 'Tackle Football — Varsity Chiefs (11U)',
    logoUrl: 'https://customer-assets.emergentagent.com/job_tesa-pcc-build/artifacts/i0lfno9g_APP_LOGO_Tackle_Football-Varsity-Chiefs_11U.png'
  },
  div_senior_chiefs: {
    id: 'div_senior_chiefs',
    programType: 'tackle_football',
    name: 'Senior Chiefs',
    ageRange: '12-12',
    displayName: 'Tackle Football — Senior Chiefs (12U)',
    logoUrl: 'https://customer-assets.emergentagent.com/job_tesa-pcc-build/artifacts/3pnmzmsd_APP_LOGO_Tackle-Football_Senior-Chiefs_12U.png'
  },
  div_elite_chiefs: {
    id: 'div_elite_chiefs',
    programType: 'tackle_football',
    name: 'Elite Chiefs',
    ageRange: '13-13',
    displayName: 'Tackle Football — Elite Chiefs (13U)',
    logoUrl: 'https://customer-assets.emergentagent.com/job_tesa-pcc-build/artifacts/g8wowyf5_APP_LOGO_Tackle-Football_Elite-Chiefs_13U.png'
  },
  // Cheer - The Lady Chiefs
  div_cheer_sparks: {
    id: 'div_cheer_sparks',
    programType: 'cheer',
    name: 'Cheer Sparks',
    ageRange: '5-8',
    displayName: 'Cheer — The Lady Chiefs: Cheer Sparks (5–8)',
    logoUrl: 'https://customer-assets.emergentagent.com/job_tesa-pcc-build/artifacts/zmjfmjks_APP_LOGO_Cheer_The-Lady-Chiefs-Cheer-Sparks_5_8.png'
  },
  div_cheer_stars: {
    id: 'div_cheer_stars',
    programType: 'cheer',
    name: 'Cheer Stars',
    ageRange: '9-11',
    displayName: 'Cheer — The Lady Chiefs: Cheer Stars (9–11)',
    logoUrl: 'https://customer-assets.emergentagent.com/job_tesa-pcc-build/artifacts/hv4aco7r_APP_LOGO_Cheer_The-Lady-Chiefs-Cheer-Stars_9_11.png'
  },
  div_cheer_elite: {
    id: 'div_cheer_elite',
    programType: 'cheer',
    name: 'Cheer Elite',
    ageRange: '12-14',
    displayName: 'Cheer — The Lady Chiefs: Cheer Elite (12–14)',
    logoUrl: 'https://customer-assets.emergentagent.com/job_tesa-pcc-build/artifacts/v5fqivvq_APP_LOGO_Cheer-The%20Lady%20Chiefs_Cheer%20Elite_12_14.png'
  }
};

// Program pricing structure
const PROGRAM_PRICING = {
  flag_football: {
    id: 'flag_football',
    name: 'Flag Football',
    fullName: 'Flag Football — Little Chiefs',
    description: 'Non-contact flag football for ages 5–7',
    standardPrice: 200.00,
    earlyBirdPrice: 150.00,
    siblingPrice: 150.00,
    icon: Flag,
    ageRange: '5-7',
    color: 'bg-blue-500',
    divisions: ['div_little_chiefs']
  },
  tackle_football: {
    id: 'tackle_football',
    name: 'Tackle Football',
    fullName: 'Tackle Football',
    description: 'Traditional tackle football with full pads for ages 7–13',
    standardPrice: 300.00,
    earlyBirdPrice: 225.00,
    siblingPrice: 225.00,
    icon: Shield,
    ageRange: '7-13',
    color: 'bg-navy',
    divisions: ['div_tiny_chiefs', 'div_mighty_chiefs', 'div_junior_chiefs', 'div_varsity_chiefs', 'div_senior_chiefs', 'div_elite_chiefs']
  },
  cheer: {
    id: 'cheer',
    name: 'Cheer — The Lady Chiefs',
    fullName: 'Cheer — The Lady Chiefs',
    description: 'Cheerleading and spirit squad for ages 5–14',
    standardPrice: 400.00,
    earlyBirdPrice: 325.00,
    siblingPrice: 325.00,
    icon: Sparkles,
    ageRange: '5-14',
    color: 'bg-red',
    divisions: ['div_cheer_sparks', 'div_cheer_stars', 'div_cheer_elite']
  }
};

// Get division for a player based on age and program
const getDivisionForPlayer = (playerAge, programType) => {
  if (!playerAge || !programType) return null;
  
  const program = PROGRAM_PRICING[programType];
  if (!program) return null;
  
  for (const divId of program.divisions) {
    const div = DIVISIONS[divId];
    const [minAge, maxAge] = div.ageRange.split('-').map(Number);
    if (playerAge >= minAge && playerAge <= maxAge) {
      return div;
    }
  }
  return null;
};

// Legacy compatibility - map to new structure
const PROGRAM_OPTIONS = Object.fromEntries(
  Object.entries(PROGRAM_PRICING).map(([key, val]) => [
    key, 
    { ...val, amount: val.standardPrice }
  ])
);

// Helper to calculate age from date of birth.
// Uses parseLocalDate so "YYYY-MM-DD" is treated as a LOCAL calendar date,
// preventing the UTC-midnight off-by-one shift for players born near
// month boundaries.
const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  const birthDate = parseLocalDate(dateOfBirth);
  if (!birthDate || Number.isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Check if player age is within program age range
const isAgeEligible = (playerAge, ageRange) => {
  if (playerAge === null) return true; // Allow if no DOB
  const [minAge, maxAge] = ageRange.split('-').map(Number);
  return playerAge >= minAge && playerAge <= maxAge;
};

// Payment methods
const PAYMENT_METHODS = {
  cash_app: {
    id: 'cash_app',
    name: 'Cash App',
    icon: '💵',
    handle: '$TakeoverEliteSports',
    instructions: 'Click the button below to open Cash App with the payment amount pre-filled.',
    hasDirectLink: true,
    getPaymentLink: (amount, memo) => `https://cash.app/$TakeoverEliteSports/${amount}?note=${encodeURIComponent(memo)}`
  },
  venmo: {
    id: 'venmo',
    name: 'Venmo',
    icon: '📱',
    handle: '@TakeoverEliteSportsAcademy',
    instructions: 'Click the button below to open Venmo with the payment details pre-filled.',
    hasDirectLink: true,
    getPaymentLink: (amount, memo) => `https://venmo.com/u/TakeoverEliteSportsAcademy?txn=pay&amount=${amount}&note=${encodeURIComponent(memo)}`
  },
  zelle: {
    id: 'zelle',
    name: 'Zelle',
    icon: '🏦',
    handle: 'takeoverelitesportsacademy@gmail.com',
    instructions: 'Open your banking app, go to Zelle, and send payment to the email below. Include the player info in the memo.',
    hasDirectLink: false
  },
  clover_card: {
    id: 'clover_card',
    name: 'Credit/Debit Card',
    icon: '💳',
    handle: null,
    instructions: 'Pay securely with credit or debit card via Clover',
    hasDirectLink: false
  },
  shopify_payment_plan: {
    id: 'shopify_payment_plan',
    name: 'Pay Later / Payment Plan',
    icon: '🗓️',
    handle: 'via Shopify Checkout',
    instructions: 'Complete your registration payment through Shopify Checkout. Flexible payment options, including installment plans, may be available based on eligibility and checkout availability.',
    hasDirectLink: true,
    getPaymentLink: (amount, memo, programLabel) => {
      const urls = {
        Flag: process.env.REACT_APP_SHOPIFY_PAYMENT_URL_FLAG,
        Tackle: process.env.REACT_APP_SHOPIFY_PAYMENT_URL_TACKLE,
        Cheer: process.env.REACT_APP_SHOPIFY_PAYMENT_URL_CHEER,
      };
      const base = urls[programLabel];
      if (!base) return '#';
      const sep = base.includes('?') ? '&' : '?';
      return `${base}${sep}note=${encodeURIComponent(memo)}`;
    }
  }
};

const STEPS = [
  { id: 'program_type', name: 'Program', description: 'Select program' },
  { id: 'player_basics', name: 'Player Info', description: 'Basic information' },
  { id: 'school_grade', name: 'School', description: 'School and grade' },
  { id: 'medical_emergency', name: 'Medical', description: 'Medical info' },
  { id: 'division', name: 'Division', description: 'Select division' },
  { id: 'jersey_details', name: 'Jersey', description: 'Size and preferences' },
  { id: 'documents', name: 'Documents', description: 'Upload required docs' },
  { id: 'waivers', name: 'Waivers', description: 'Sign waivers' },
  { id: 'payment', name: 'Payment', description: 'Registration fee' },
  { id: 'review', name: 'Review', description: 'Review and submit' }
];

// Per-waiver structured submission fields. The PDF is a reference document only —
// these field values + the acknowledgment + the signature constitute the real record.
// Acknowledgment statements (shown in UI under the certification checkbox).
const WAIVER_ACK_STATEMENTS = {
  participation: [
    'I am the parent/legal guardian or authorized adult for the participant.',
    'I have reviewed the risks associated with participation.',
    'I understand that participation includes program activities, direct team needs, league events, and fundraisers.',
    'I agree to the assumption of risk, waiver, and release of liability above.',
    'I understand that participation is voluntary and applies to the selected program/division.'
  ],
  medical_consent: [
    'I authorize Park City Chiefs and Takeover Elite Sports Academy staff, coaches, volunteers, and authorized representatives to seek emergency medical evaluation and treatment for the participant if I cannot be reached in a timely manner.',
    'I authorize emergency medical personnel, physicians, nurses, urgent care providers, and hospital staff to provide emergency treatment considered necessary for the health and safety of my child.'
  ],
  code_of_conduct: [
    'I understand that participation in Park City Chiefs programs is conditioned on following program rules, safety expectations, attendance requirements, communication standards, and the signed Participation Form, including its assumption of risk, waiver, and release of liability terms.',
    'I understand that failure to follow the Park City Chiefs Code of Conduct, safety rules, coach instructions, equipment rules, attendance expectations, or parent/guardian conduct standards may result in disciplinary action, suspension, removal from activities, or removal from the program.'
  ]
};

const WAIVER_FIELD_DEFS = {
  participation: [
    { key: 'participant_name', label: 'Participant Name', type: 'text', required: true, prefill: 'participant_name' },
    { key: 'participant_dob', label: 'Participant Date of Birth', type: 'date', required: true, prefill: 'participant_dob' },
    { key: 'parent_guardian_name', label: 'Parent/Legal Guardian Full Name', type: 'text', required: true, prefill: 'parent_name' },
    { key: 'relationship_to_participant', label: 'Relationship to Participant', type: 'select', required: true, options: ['Mother', 'Father', 'Legal Guardian', 'Other'] },
    { key: 'contact_phone', label: 'Phone / Email', type: 'text', required: true, prefill: 'parent_contact', placeholder: 'Phone number or email' },
    { key: 'media_permission', label: 'Media Permission (photos/videos for PCC/TESA team, website, social media, promotional, and community purposes)', type: 'radio', required: true, options: [
      { value: 'yes', label: 'I give permission' },
      { value: 'no', label: 'I do not give media permission at this time' }
    ] }
  ],
  medical_consent: [
    { key: 'participant_name', label: 'Participant Name', type: 'text', required: true, prefill: 'participant_name' },
    { key: 'participant_dob', label: 'Participant Date of Birth', type: 'date', required: true, prefill: 'participant_dob' },
    { key: 'emergency_contact_name', label: 'Emergency Contact Name', type: 'text', required: true, placeholder: 'Full name' },
    { key: 'emergency_contact_phone', label: 'Emergency Contact Phone', type: 'tel', required: true, placeholder: '(555) 555-5555' },
    { key: 'medical_conditions', label: 'Medical Conditions / Allergies / Medications', type: 'textarea', required: true, prefill: 'medical_notes', placeholder: 'List any allergies, conditions, and current medications. Enter "None" if none.' }
  ],
  code_of_conduct: [
    { key: 'participant_name', label: 'Participant Name', type: 'text', required: true, prefill: 'participant_name' },
    { key: 'participant_dob', label: 'Participant Date of Birth', type: 'date', required: true, prefill: 'participant_dob' },
    { key: 'parent_guardian_name', label: 'Parent/Legal Guardian Full Name', type: 'text', required: true, prefill: 'parent_name' },
    { key: 'contact_phone', label: 'Phone / Email', type: 'text', required: true, prefill: 'parent_contact', placeholder: 'Phone number or email' }
  ]
};

const waiverFieldsComplete = (waiverType, values) => {
  const defs = WAIVER_FIELD_DEFS[waiverType] || [];
  return defs.every(f => !f.required || (values && (values[f.key] !== undefined && values[f.key] !== null && String(values[f.key]).trim() !== '')));
};

// Build prefilled values for a given waiver type using player + parent user data
const buildWaiverPrefill = (waiverType, player, parentUser) => {
  const sources = {
    participant_name: ((player?.first_name || '') + ' ' + (player?.last_name || '')).trim(),
    participant_dob: player?.date_of_birth || '',
    parent_name: parentUser?.name || '',
    parent_contact: parentUser?.phone || parentUser?.email || '',
    medical_notes: player?.medical_notes || ''
  };
  const defs = WAIVER_FIELD_DEFS[waiverType] || [];
  const out = {};
  defs.forEach(f => { if (f.prefill && sources[f.prefill]) out[f.key] = sources[f.prefill]; });
  return out;
};

const RegistrationWizard = () => {
  const { registrationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [registration, setRegistration] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Payment state
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState(null);

  // Waiver modal state
  const [waiverModal, setWaiverModal] = useState({ open: false, type: null, pdfUrl: null, loading: false });
  const [waiverViewed, setWaiverViewed] = useState(false);
  const [waiverAcknowledged, setWaiverAcknowledged] = useState(false);
  const [waiverSignature, setWaiverSignature] = useState('');
  const [waiverFieldValues, setWaiverFieldValues] = useState({});

  // Live program configuration from /api/program-configs. When null/empty,
  // the renderer falls back to the static PROGRAM_PRICING defaults so the
  // page never crashes if the network request fails.
  const [programConfigs, setProgramConfigs] = useState(null);

  useEffect(() => {
    fetchRegistration();
    fetchPaymentSettings();
    fetchProgramConfigs();
  }, [registrationId]);

  const fetchProgramConfigs = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/program-configs`, { withCredentials: true });
      if (Array.isArray(r.data) && r.data.length) setProgramConfigs(r.data);
    } catch (e) {
      // Graceful fallback — keep using PROGRAM_PRICING defaults.
    }
  };

  // Merge live config values over the static visual defaults. Keys: icon,
  // color, divisions, ageRange, fullName stay from PROGRAM_PRICING; prices,
  // description, name, includes, active toggle come from the DB.
  const programs = useMemo(() => {
    if (!programConfigs) return PROGRAM_PRICING;
    const out = {};
    for (const cfg of programConfigs) {
      const defaults = PROGRAM_PRICING[cfg.program_key];
      if (!defaults) continue;
      if (cfg.active === false) continue;
      out[cfg.program_key] = {
        ...defaults,
        name: cfg.name || defaults.name,
        fullName: cfg.name || defaults.fullName,
        description: cfg.description || defaults.description,
        standardPrice: cfg.standard_price ?? defaults.standardPrice,
        earlyBirdPrice: cfg.early_bird_price ?? defaults.earlyBirdPrice,
        siblingPrice: cfg.sibling_price ?? defaults.siblingPrice,
        includes: Array.isArray(cfg.includes) ? cfg.includes : [],
        earlyBirdDeadlineISO: cfg.early_bird_deadline || null,
      };
    }
    return Object.keys(out).length ? out : PROGRAM_PRICING;
  }, [programConfigs]);

  // Earliest-active-deadline used as the season-wide early-bird gate.
  const effectiveEarlyBirdDeadline = useMemo(() => {
    if (!programConfigs || !programConfigs.length) return EARLY_BIRD_DEADLINE;
    const dates = programConfigs
      .filter(c => c.early_bird_deadline)
      .map(c => {
        const [y, m, d] = String(c.early_bird_deadline).split('-').map(Number);
        return y && m && d ? new Date(y, m - 1, d, 23, 59, 59) : null;
      })
      .filter(Boolean);
    if (!dates.length) return EARLY_BIRD_DEADLINE;
    return new Date(Math.max(...dates.map(d => d.getTime())));
  }, [programConfigs]);

  const fetchRegistration = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/registrations/${registrationId}/detail`,
        { withCredentials: true }
      );
      setRegistration(response.data);
      
      // Set current step based on progress
      const completedCount = response.data.steps_completed?.length || 0;
      let initialStep = Math.min(completedCount, STEPS.length - 1);

      // Deep-link support: `?step=<step_id>` overrides the
      // completion-based default when the caller knows where to land
      // (e.g. parent clicking "Pay balance" from FamilyPage). Falls
      // back silently if the param is missing or unknown.
      const stepParam = new URLSearchParams(location.search).get('step');
      if (stepParam) {
        const idx = STEPS.findIndex(s => s.id === stepParam);
        if (idx >= 0) initialStep = idx;
      }
      setCurrentStep(initialStep);
      
      // Pre-fill form data from registration_data OR from player profile
      const regData = response.data.registration_data || {};
      const player = response.data.player || {};
      
      // Pre-fill school_grade step from player profile if not already set
      if (!regData.school_grade) {
        regData.school_grade = {
          school: player.school || '',
          grade: player.grade || ''
        };
      }
      
      // Pre-fill medical_emergency step from player profile if not already set
      if (!regData.medical_emergency) {
        regData.medical_emergency = {
          medical_notes: player.medical_notes || '',
          emergency_contact: player.emergency_contact || '',
          emergency_phone: player.emergency_phone || ''
        };
      }
      
      // Pre-fill jersey_details step from player profile if not already set
      if (!regData.jersey_details) {
        regData.jersey_details = {
          jersey_size: player.jersey_size || '',
          jersey_number: player.jersey_number || ''
        };
      }
      
      setFormData(regData);
      
      // Set selected payment method if already chosen
      if (response.data.payment_method) {
        setSelectedPaymentMethod(response.data.payment_method);
      }
    } catch (error) {
      console.error('Failed to fetch registration:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/registrations/${registrationId}/payment-info`,
        { withCredentials: true }
      );
      setPaymentSettings(response.data.payment_settings);
    } catch (error) {
      console.error('Failed to fetch payment settings:', error);
    }
  };

  const saveStep = async (stepId, data) => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/registrations/${registrationId}/step`,
        { step_name: stepId, data },
        { withCredentials: true }
      );
      setFormData(prev => ({ ...prev, [stepId]: data }));
      await fetchRegistration();
    } catch (error) {
      console.error('Failed to save step:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    const step = STEPS[currentStep];
    const stepData = formData[step.id] || {};
    
    // Validation for program_type step
    if (step.id === 'program_type' && !stepData.program_type) {
      alert('Please select a program type to continue');
      return;
    }
    
    await saveStep(step.id, stepData);
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    // Check if payment is completed
    const paymentStatus = registration?.payment_status;
    if (!['confirmed', 'waived'].includes(paymentStatus)) {
      alert('Payment must be confirmed before submitting registration. If you have already submitted payment, please wait for admin confirmation.');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/registrations/${registrationId}/submit`,
        {},
        { withCredentials: true }
      );
      navigate('/family');
    } catch (error) {
      console.error('Failed to submit:', error);
      alert(error.response?.data?.detail || 'Failed to submit');
    } finally {
      setSaving(false);
    }
  };

  const openWaiverModal = async (waiverType) => {
    setWaiverViewed(false);
    setWaiverAcknowledged(false);
    setWaiverSignature('');
    setWaiverFieldValues(buildWaiverPrefill(waiverType, registration?.player, user));
    setWaiverModal({ open: true, type: waiverType, pdfUrl: null, loading: true });

    try {
      const res = await axios.get(`${API_URL}/api/waivers/pdf/${waiverType}`, { withCredentials: true });
      setWaiverModal(prev => ({ ...prev, pdfUrl: res.data.pdf_url, loading: false }));
      setWaiverViewed(true);
    } catch {
      setWaiverModal(prev => ({ ...prev, loading: false }));
      setWaiverViewed(true);
    }
  };

  const handleSignWaiver = async () => {
    if (!waiverSignature.trim()) return;
    const savedStep = currentStep;
    try {
      await axios.post(
        `${API_URL}/api/waivers/sign`,
        {
          registration_id: registrationId,
          waiver_type: waiverModal.type,
          signature: waiverSignature.trim(),
          field_values: waiverFieldValues
        },
        { withCredentials: true }
      );
      setWaiverModal({ open: false, type: null, pdfUrl: null, loading: false });
      await fetchRegistration();
      setCurrentStep(savedStep);
    } catch (error) {
      console.error('Failed to sign:', error);
    }
  };

  const handleUploadDocument = async (documentType) => {
    try {
      await axios.post(
        `${API_URL}/api/documents/upload`,
        { registration_id: registrationId, document_type: documentType, file_url: 'placeholder.pdf' },
        { withCredentials: true }
      );
      await fetchRegistration();
    } catch (error) {
      console.error('Failed to upload:', error);
    }
  };

  const handleSubmitPayment = async (method) => {
    if (method === 'clover_card') {
      // Clover card handled by inline CloverPaymentForm component
      return;
    }
    
    setPaymentSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/registrations/${registrationId}/payment/submit`,
        { payment_method: method },
        { withCredentials: true }
      );
      await fetchRegistration();
      setSelectedPaymentMethod(method);
    } catch (error) {
      console.error('Failed to submit payment:', error);
      alert(error.response?.data?.detail || 'Failed to submit payment');
    } finally {
      setPaymentSubmitting(false);
    }
  };

  // Get selected program (merged live config + visual defaults)
  const selectedProgram = formData.program_type?.program_type 
    ? programs[formData.program_type.program_type]
    : null;

  // Check if early bird pricing applies (uses hydrated deadline)
  const isEarlyBird = () => {
    const now = new Date();
    return now <= effectiveEarlyBirdDeadline;
  };

  // Check if sibling discount applies (has other players in household with registrations)
  const hasSiblingDiscount = () => {
    return registration?.sibling_discount_eligible === true;
  };

  // Calculate payment amount based on program, early bird, and sibling discount
  const calculatePricing = () => {
    if (!selectedProgram) {
      return {
        standardPrice: registration?.total_amount || 0,
        finalAmount: registration?.total_amount || 0,
        earlyBirdApplied: false,
        siblingDiscountApplied: false,
        discountReason: null
      };
    }

    const standardPrice = selectedProgram.standardPrice;
    const earlyBird = isEarlyBird();
    const siblingEligible = hasSiblingDiscount();
    
    let finalAmount = standardPrice;
    let earlyBirdApplied = false;
    let siblingDiscountApplied = false;
    let discountReason = null;

    // Sibling discount takes priority (same as early bird price)
    if (siblingEligible) {
      finalAmount = selectedProgram.siblingPrice;
      siblingDiscountApplied = true;
      discountReason = 'Sibling Discount';
    } else if (earlyBird) {
      finalAmount = selectedProgram.earlyBirdPrice;
      earlyBirdApplied = true;
      discountReason = 'Early Bird Pricing';
    }

    return {
      standardPrice,
      earlyBirdPrice: selectedProgram.earlyBirdPrice,
      siblingPrice: selectedProgram.siblingPrice,
      finalAmount,
      earlyBirdApplied,
      siblingDiscountApplied,
      discountReason,
      savings: standardPrice - finalAmount
    };
  };

  const pricing = calculatePricing();

  // Calculate payment amount based on program
  const getPaymentAmount = () => {
    return pricing.finalAmount;
  };

  const getPaymentStatusBadge = (status) => {
    const statusMap = {
      'not_started': { label: 'Not Started', className: 'bg-gray-100 text-gray-800', icon: Circle },
      'pending': { label: 'Pending', className: 'bg-yellow-100 text-yellow-800', icon: Clock },
      'submitted': { label: 'Submitted - Awaiting Confirmation', className: 'bg-blue-100 text-blue-800', icon: Clock },
      'confirmed': { label: 'Confirmed', className: 'bg-green-100 text-green-800', icon: CheckCircle },
      'failed': { label: 'Failed', className: 'bg-red-100 text-red-800', icon: AlertCircle },
      'waived': { label: 'Waived', className: 'bg-purple-100 text-purple-800', icon: CheckCircle }
    };
    return statusMap[status] || statusMap['not_started'];
  };

  const renderStepContent = () => {
    const step = STEPS[currentStep];
    const stepData = formData[step.id] || {};
    const earlyBird = isEarlyBird();
    const siblingEligible = hasSiblingDiscount();

    // PROGRAM TYPE STEP - Hero pricing page
    if (step.id === 'program_type') {
      const earlyBirdDeadline = effectiveEarlyBirdDeadline;
      const daysUntilDeadline = Math.ceil((earlyBirdDeadline - new Date()) / (1000 * 60 * 60 * 24));
      const deadlineLabel = earlyBirdDeadline.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
      // Compute the largest possible savings across active programs for the
      // "save up to $X" copy in the explainer card.
      const maxSavings = Object.values(programs).reduce((max, p) => {
        const s = (p.standardPrice || 0) - (p.earlyBirdPrice || 0);
        return s > max ? s : max;
      }, 0);
      
      return (
        <div className="space-y-6" data-testid="program-type-step">
          {/* Hero Pricing Header */}
          <div className="bg-navy text-white rounded-xl p-6 -mx-2 sm:mx-0 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gold/10 rounded-full -translate-y-16 translate-x-16"></div>
            <div className="relative text-center">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-2">Park City Chiefs</p>
              <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tighter mb-2">Fall 2026 Registration</h1>
              <p className="text-white/70 text-lg font-body">
                {registration?.player?.first_name} {registration?.player?.last_name}
              </p>
              
              {/* Early Bird / Sibling Discount Badges */}
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {earlyBird && (
                  <div className="bg-green-500 text-white px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider flex items-center">
                    <Tag className="w-4 h-4 mr-2" />
                    Early Bird - {daysUntilDeadline > 0 ? `${daysUntilDeadline} days left!` : 'Ends Today!'}
                  </div>
                )}
                {siblingEligible && (
                  <div className="bg-purple-500 text-white px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider flex items-center">
                    <Users className="w-4 h-4 mr-2" />
                    Sibling Discount
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Pricing Explanation */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <DollarSign className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-semibold text-amber-800 mb-1">How Pricing Works</p>
                <ul className="text-amber-700 space-y-1">
                  {earlyBird && (
                    <li>• <strong>Early Bird:</strong> Register by {deadlineLabel} to save up to ${maxSavings}!</li>
                  )}
                  {siblingEligible && (
                    <li>• <strong>Sibling Discount:</strong> Additional players in your household get the early bird rate!</li>
                  )}
                  {!earlyBird && !siblingEligible && (
                    <li>• Early bird pricing ended {deadlineLabel}. Standard pricing applies.</li>
                  )}
                  <li>• You can create profiles and start registration before paying.</li>
                  <li>• Registration is finalized once payment is confirmed.</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Program Selection Header */}
          <div className="text-center">
            <h2 className="font-display text-2xl font-black text-navy uppercase tracking-tight">Choose Your Program</h2>
            <p className="text-gray-600 mt-1 font-body">Select a program to see your total</p>
            {registration?.player?.date_of_birth && (
              <p className="text-sm text-gray-500 mt-1 font-body">
                {registration?.player?.first_name} is {calculateAge(registration?.player?.date_of_birth)} years old
              </p>
            )}
          </div>
          
          {/* Program Cards with Prominent Pricing */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.values(programs).map((program) => {
              const Icon = program.icon;
              const isSelected = stepData.program_type === program.id;
              const playerAge = calculateAge(registration?.player?.date_of_birth);
              const eligible = isAgeEligible(playerAge, program.ageRange);
              const playerDivision = getDivisionForPlayer(playerAge, program.id);
              
              // Calculate display price for this program
              let displayPrice = program.standardPrice;
              let hasDiscount = false;
              let discountLabel = '';
              if (siblingEligible) {
                displayPrice = program.siblingPrice;
                hasDiscount = true;
                discountLabel = 'Sibling Price';
              } else if (earlyBird) {
                displayPrice = program.earlyBirdPrice;
                hasDiscount = true;
                discountLabel = 'Early Bird';
              }
              const savings = program.standardPrice - displayPrice;
              
              return (
                <button
                  key={program.id}
                  onClick={() => {
                    if (!eligible) {
                      alert(`${registration?.player?.first_name} is ${playerAge} years old and not eligible for ${program.name} (Ages ${program.ageRange}).`);
                      return;
                    }
                    setFormData({
                      ...formData,
                      [step.id]: { 
                        ...stepData, 
                        program_type: program.id,
                        division_id: playerDivision?.id,
                        division_name: playerDivision?.displayName,
                        division_logo: playerDivision?.logoUrl
                      }
                    });
                  }}
                  disabled={!eligible}
                  className={`relative rounded-xl border-2 text-left transition-all overflow-hidden ${
                    !eligible
                      ? 'border-gray-200 bg-gray-100 opacity-60 cursor-not-allowed'
                      : isSelected 
                        ? 'border-navy ring-2 ring-navy/20 shadow-xl scale-[1.02]' 
                        : 'border-gray-200 hover:border-navy/50 hover:shadow-lg'
                  }`}
                  data-testid={`program-${program.id}`}
                >
                  {/* Age Restriction Banner */}
                  {!eligible && (
                    <div className="absolute top-0 left-0 right-0 bg-red-500 text-white text-center py-1 text-xs font-semibold">
                      NOT ELIGIBLE (Ages {program.ageRange})
                    </div>
                  )}
                  
                  {/* Selection Indicator */}
                  {isSelected && eligible && (
                    <div className="absolute top-0 left-0 right-0 bg-navy text-white text-center py-1 text-xs font-semibold">
                      SELECTED
                    </div>
                  )}
                  
                  <div className={`p-5 ${(isSelected && eligible) || !eligible ? 'pt-8' : ''}`}>
                    {/* Program Icon & Name */}
                    <div className="flex items-center space-x-3 mb-3">
                      <div className={`w-12 h-12 rounded-full ${eligible ? program.color : 'bg-gray-400'} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className={`text-lg font-bold ${eligible ? 'text-navy' : 'text-gray-500'}`}>{program.name}</h3>
                        <p className={`text-xs ${eligible ? 'text-gray-500' : 'text-red-500 font-medium'}`}>Ages {program.ageRange}</p>
                      </div>
                    </div>
                    
                    <p className={`text-sm mb-3 ${eligible ? 'text-gray-600' : 'text-gray-400'}`}>{program.description}</p>
                    
                    {/* Registration Includes */}
                    {Array.isArray(program.includes) && program.includes.length > 0 && (
                      <div
                        className={`mb-3 rounded-lg border ${eligible ? 'border-gray-200 bg-gray-50' : 'border-gray-200 bg-gray-100 opacity-60'} px-3 py-2`}
                        data-testid={`program-includes-${program.id}`}
                      >
                        <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${eligible ? 'text-navy' : 'text-gray-500'}`}>
                          Registration Includes
                        </p>
                        <ul className={`text-xs space-y-0.5 ${eligible ? 'text-gray-700' : 'text-gray-500'}`}>
                          {program.includes.map((item, i) => (
                            <li key={i} className="flex items-start">
                              <Check className="w-3 h-3 mt-0.5 mr-1.5 flex-shrink-0 text-green-600" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Division Assignment Preview */}
                    {eligible && playerDivision && (
                      <div className="mb-3 px-3 py-2 bg-gold/20 rounded-lg border border-gold/30">
                        <p className="text-xs text-navy font-medium">Your Division:</p>
                        <p className="text-sm font-semibold text-navy">{playerDivision.name}</p>
                      </div>
                    )}
                    
                    {/* PROMINENT PRICING */}
                    <div className={`rounded-lg p-4 ${!eligible ? 'bg-gray-200' : hasDiscount ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                      {hasDiscount && eligible ? (
                        <>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-gray-500">Regular:</span>
                            <span className="text-sm text-gray-400 line-through">${program.standardPrice}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-green-700">{discountLabel}:</span>
                            <span className="text-2xl font-bold text-green-600">${displayPrice}</span>
                          </div>
                          <div className="text-center mt-2">
                            <Badge className="bg-green-600 text-white">
                              Save ${savings}!
                            </Badge>
                          </div>
                        </>
                      ) : (
                        <div className="text-center">
                          <span className={`text-2xl font-bold ${eligible ? 'text-navy' : 'text-gray-400'}`}>${displayPrice}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          
          {/* Live Pricing Summary - Shows when program selected */}
          {stepData.program_type && (
            <div className="bg-white border-2 border-navy rounded-xl overflow-hidden shadow-lg">
              <div className="bg-navy text-white px-6 py-3">
                <h4 className="font-display text-lg font-black uppercase tracking-tight flex items-center">
                  <DollarSign className="w-5 h-5 mr-2" />
                  Your Registration Total
                </h4>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Program:</span>
                    <span className="font-semibold text-navy">{programs[stepData.program_type]?.name || PROGRAM_PRICING[stepData.program_type]?.name}</span>
                  </div>
                  
                  {/* Show Division Assignment */}
                  {stepData.division_name && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Division:</span>
                      <div className="flex items-center space-x-2">
                        {stepData.division_logo && (
                          <img src={stepData.division_logo} alt="" className="w-6 h-6 object-contain" />
                        )}
                        <span className="font-semibold text-navy">{stepData.division_name}</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Standard Price:</span>
                    <span className={pricing.savings > 0 ? 'line-through text-gray-400' : 'font-semibold'}>
                      ${pricing.standardPrice.toFixed(2)}
                    </span>
                  </div>
                  
                  {pricing.earlyBirdApplied && (
                    <div className="flex justify-between items-center text-green-600">
                      <span className="flex items-center font-medium">
                        <Tag className="w-4 h-4 mr-2" />
                        Early Bird Discount
                      </span>
                      <span className="font-semibold">-${pricing.savings.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {pricing.siblingDiscountApplied && (
                    <div className="flex justify-between items-center text-purple-600">
                      <span className="flex items-center font-medium">
                        <Users className="w-4 h-4 mr-2" />
                        Sibling Discount
                      </span>
                      <span className="font-semibold">-${pricing.savings.toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="border-t-2 border-navy pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-navy">Amount Due:</span>
                      <span className="text-3xl font-bold text-navy">${pricing.finalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Payment Info Preview */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600 mb-2 font-medium">Payment Methods Accepted:</p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="text-xs">Cash App</Badge>
                    <Badge variant="outline" className="text-xs">Zelle</Badge>
                    <Badge variant="outline" className="text-xs">Venmo</Badge>
                    <Badge variant="outline" className="text-xs">Credit/Debit</Badge>
                  </div>
                </div>
                
                {/* What's Next */}
                <div className="mt-4 bg-blue-50 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>What's next?</strong> Complete player details, sign waivers, upload documents, and submit payment.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* No program selected message */}
          {!stepData.program_type && (
            <div className="text-center p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <DollarSign className="w-10 h-10 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">Select a program above to see your total</p>
            </div>
          )}
        </div>
      );
    }

    if (step.id === 'player_basics') {
      return (
        <div className="space-y-4">
          <p className="text-gray-600">Player: <strong>{registration?.player?.first_name} {registration?.player?.last_name}</strong></p>
          <p className="text-sm text-gray-500">Player information is pre-filled from your profile.</p>
          
          {selectedProgram && (
            <div className="mt-4 p-3 bg-navy/5 rounded-lg">
              <p className="text-sm text-navy font-medium">
                Program: {selectedProgram.name}
              </p>
            </div>
          )}
        </div>
      );
    }

    if (step.id === 'school_grade') {
      return (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-blue-800">
              This information is pre-filled from {registration?.player?.first_name}'s profile. Update if needed.
            </p>
          </div>
          <div>
            <Label>School</Label>
            <Input 
              value={stepData.school || ''} 
              onChange={(e) => setFormData({...formData, [step.id]: {...stepData, school: e.target.value}})} 
              placeholder="Enter school name"
            />
          </div>
          <div>
            <Label>Grade</Label>
            <Input 
              value={stepData.grade || ''} 
              onChange={(e) => setFormData({...formData, [step.id]: {...stepData, grade: e.target.value}})} 
              placeholder="e.g., 5th, 6th"
            />
          </div>
        </div>
      );
    }

    if (step.id === 'medical_emergency') {
      return (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-blue-800">
              This information is pre-filled from {registration?.player?.first_name}'s profile. Update if needed.
            </p>
          </div>
          <div>
            <Label>Medical Notes / Allergies</Label>
            <textarea 
              className="w-full border p-2 rounded min-h-[100px]" 
              value={stepData.medical_notes || ''} 
              onChange={(e) => setFormData({...formData, [step.id]: {...stepData, medical_notes: e.target.value}})} 
              placeholder="Enter any allergies, medical conditions, or special needs..."
            />
          </div>
          <div>
            <Label>Emergency Contact Name</Label>
            <Input 
              value={stepData.emergency_contact || ''} 
              onChange={(e) => setFormData({...formData, [step.id]: {...stepData, emergency_contact: e.target.value}})} 
              placeholder="e.g., John Smith (Father)"
            />
          </div>
          <div>
            <Label>Emergency Contact Phone</Label>
            <Input 
              value={stepData.emergency_phone || ''} 
              onChange={(e) => setFormData({...formData, [step.id]: {...stepData, emergency_phone: e.target.value}})} 
              placeholder="e.g., (555) 123-4567"
            />
          </div>
        </div>
      );
    }

    if (step.id === 'division') {
      return (
        <div className="space-y-4">
          <div className="bg-blue-50 border p-4 rounded">
            <h3 className="font-semibold mb-2">Recommended Division</h3>
            <p className="text-sm">Based on age, division has been automatically assigned.</p>
          </div>
          
          {selectedProgram && (
            <div className="p-3 bg-navy/5 rounded-lg">
              <p className="text-sm text-navy">Program: <strong>{selectedProgram.name}</strong></p>
            </div>
          )}
        </div>
      );
    }

    if (step.id === 'jersey_details') {
      return (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-blue-800">
              This information is pre-filled from {registration?.player?.first_name}'s profile. Update if needed.
            </p>
          </div>
          <div>
            <Label>Jersey Size</Label>
            <select 
              className="w-full border p-2 rounded"
              value={stepData.jersey_size || registration?.player?.jersey_size || ''}
              onChange={(e) => setFormData({...formData, [step.id]: {...stepData, jersey_size: e.target.value}})}
            >
              <option value="">Select Size</option>
              <option value="YS">Youth Small (YS)</option>
              <option value="YM">Youth Medium (YM)</option>
              <option value="YL">Youth Large (YL)</option>
              <option value="YXL">Youth XL (YXL)</option>
              <option value="AS">Adult Small (AS)</option>
              <option value="AM">Adult Medium (AM)</option>
              <option value="AL">Adult Large (AL)</option>
            </select>
          </div>
          <div>
            <Label>Preferred Jersey Number (if available)</Label>
            <Input 
              value={stepData.jersey_number || registration?.player?.jersey_number || ''} 
              onChange={(e) => setFormData({...formData, [step.id]: {...stepData, jersey_number: e.target.value}})}
              placeholder="e.g., 12"
            />
            <p className="text-xs text-gray-500 mt-1">Jersey numbers are assigned by coaches and may not match your preference.</p>
          </div>
        </div>
      );
    }

    if (step.id === 'documents') {
      return (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h3 className="font-semibold text-blue-900 flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              Required Documents
            </h3>
            <p className="text-sm text-blue-700 mt-1">
              Please upload clear photos or scans. Accepted formats: PDF, JPEG, PNG, HEIC (max 5MB each). All documents are encrypted and stored securely.
            </p>
          </div>
          
          {['birth_certificate', 'report_card', 'medical_consent_form'].map(docType => {
            const doc = registration?.documents?.find(d => d.document_type === docType);
            const docLabels = {
              birth_certificate: { name: 'Birth Certificate', desc: 'Official birth certificate for age verification', icon: FileText },
              report_card: { name: 'Report Card', desc: 'Most recent report card for grade verification', icon: FileText },
              medical_consent_form: { name: 'Medical Consent Form', desc: 'Signed medical consent and emergency contact form', icon: FileText }
            };
            const DocIcon = docLabels[docType].icon;
            
            return (
              <div key={docType} className="border-2 border-dashed border-gray-300 rounded-xl p-6 hover:border-navy/50 transition-colors">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-navy/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <DocIcon className="w-5 h-5 text-navy" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy text-lg">{docLabels[docType].name}</h4>
                      <p className="text-sm text-gray-600">{docLabels[docType].desc}</p>
                    </div>
                  </div>
                  <Badge className={doc?.status === 'approved' ? 'bg-green-100 text-green-800' : doc ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'}>
                    {doc?.status === 'approved' ? 'Approved' : doc?.status === 'pending_review' ? 'Pending Review' : 'Not Uploaded'}
                  </Badge>
                </div>
                
                {doc && doc.filename && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-green-800">{doc.filename}</p>
                        <p className="text-xs text-green-600">{doc.file_size ? `${(doc.file_size / 1024).toFixed(1)} KB` : 'Uploaded'}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="flex items-center gap-3">
                  <label className="flex-1">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        // Validate file size
                        if (file.size > 5 * 1024 * 1024) {
                          alert('File too large. Maximum size is 5MB.');
                          return;
                        }
                        
                        // Validate file type
                        const ext = file.name.split('.').pop()?.toLowerCase();
                        if (!['pdf', 'jpg', 'jpeg', 'png', 'heic', 'heif'].includes(ext)) {
                          alert('Invalid file type. Please upload PDF, JPEG, PNG, or HEIC.');
                          return;
                        }
                        
                        try {
                          // Read file as base64
                          const reader = new FileReader();
                          reader.onload = async (event) => {
                            const base64Data = event.target.result;
                            
                            await axios.post(
                              `${API_URL}/api/documents/upload`,
                              { 
                                registration_id: registrationId, 
                                document_type: docType,
                                file_data: base64Data,
                                filename: file.name,
                                content_type: file.type
                              },
                              { withCredentials: true }
                            );
                            
                            // Refresh registration to show uploaded doc
                            fetchRegistration();
                            alert(`${docLabels[docType].name} uploaded successfully!`);
                          };
                          reader.readAsDataURL(file);
                        } catch (error) {
                          console.error('Upload error:', error);
                          alert(error.response?.data?.detail || 'Failed to upload document');
                        }
                      }}
                      data-testid={`upload-${docType}`}
                    />
                    <div className="cursor-pointer bg-white border-2 border-navy/20 hover:border-navy/50 rounded-lg p-4 text-center transition-colors">
                      <Upload className="w-8 h-8 text-navy mx-auto mb-2" />
                      <p className="text-sm font-medium text-navy">
                        {doc ? 'Replace Document' : 'Click to Upload'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">PDF, JPEG, PNG, HEIC (max 5MB)</p>
                    </div>
                  </label>
                </div>
              </div>
            );
          })}
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
            <div className="flex items-start">
              <Shield className="w-5 h-5 text-green-700 mr-2 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-800">Secure Storage</p>
                <p className="text-sm text-green-700">All documents are encrypted and stored securely. Only authorized administrators can view your uploaded documents.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (step.id === 'waivers') {
      const waiverLabels = {
        participation: { name: 'Participation & Liability', desc: 'Review the participation and liability waiver before signing', hasPdf: true },
        medical_consent: { name: 'Emergency Medical Treatment Consent', desc: 'Review and sign the emergency medical treatment consent form', hasPdf: true },
        code_of_conduct: { name: 'Code of Conduct', desc: 'Review the organization code of conduct before signing', hasPdf: true }
      };

      return (
        <div className="space-y-4">
          <h3 className="font-semibold text-navy text-lg">Required Waivers</h3>
          <p className="text-sm text-gray-600 mb-4">Each waiver must be reviewed and signed. Waivers with a document will require you to view the full document before signing.</p>

          {['participation', 'medical_consent', 'code_of_conduct'].map(waiverType => {
            const waiver = registration?.waivers?.find(w => w.waiver_type === waiverType);
            const label = waiverLabels[waiverType];
            const isSigned = waiver?.status === 'signed';
            return (
              <div key={waiverType} className={`border rounded-xl p-5 transition-colors ${isSigned ? 'border-green-200 bg-green-50/50' : 'border-gray-200 hover:border-navy/30'}`} data-testid={`waiver-${waiverType}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-semibold text-navy">{label.name}</p>
                    <p className="text-sm text-gray-500 mt-1">{label.desc}</p>
                    {isSigned && waiver.signed_at && (
                      <p className="text-xs text-green-600 mt-2 flex items-center">
                        <CheckCircle className="w-3.5 h-3.5 mr-1" />
                        Signed by {waiver.signature} on {new Date(waiver.signed_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="ml-3 flex-shrink-0">
                    {isSigned ? (
                      <Badge className="bg-green-100 text-green-800">Signed</Badge>
                    ) : (
                      <Button
                        size="sm"
                        className="bg-red hover:bg-red/90 text-white"
                        onClick={() => openWaiverModal(waiverType)}
                        data-testid={`sign-waiver-${waiverType}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Review & Sign
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Waiver PDF Viewer Modal */}
          {waiverModal.open && (
            <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="waiver-modal">
              <div className="bg-white w-full sm:max-w-2xl sm:rounded-xl rounded-t-xl max-h-[95vh] flex flex-col overflow-hidden">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-navy text-white sm:rounded-t-xl">
                  <h3 className="font-bold text-lg">
                    {waiverLabels[waiverModal.type]?.name || 'Waiver'}
                  </h3>
                  <button
                    onClick={() => setWaiverModal({ open: false, type: null, pdfUrl: null, loading: false })}
                    className="text-white/70 hover:text-white p-1"
                    data-testid="waiver-modal-close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Reference PDF link (small, non-blocking) */}
                <div className="px-4 pt-3 pb-2 border-b border-gray-100 bg-gray-50">
                  {waiverModal.pdfUrl ? (
                    <a
                      href={waiverModal.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-navy hover:underline"
                      data-testid="waiver-reference-pdf-link"
                    >
                      <FileText className="w-4 h-4 mr-1" /> View reference PDF
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  ) : (
                    <span className="text-xs text-gray-500">{waiverModal.loading ? 'Loading reference document…' : 'Reference document unavailable'}</span>
                  )}
                </div>

                {/* Fields-first body: structured fields → acknowledgment → signature */}
                <div className="flex-1 overflow-auto p-4 space-y-4 bg-white">
                  {/* Per-waiver field inputs (primary record of submission) */}
                  {(() => {
                    const fieldDefs = WAIVER_FIELD_DEFS[waiverModal.type] || [];
                    if (fieldDefs.length === 0) return null;
                    return (
                      <div className="space-y-3" data-testid={`waiver-fields-${waiverModal.type}`}>
                        <div>
                          <p className="text-sm font-semibold text-navy">Please complete the following</p>
                          <p className="text-xs text-gray-500 mt-0.5">These entries are the official record of this waiver. Some fields may be prefilled from your account — please verify they are correct.</p>
                        </div>
                        {fieldDefs.map(f => (
                          <div key={f.key} className="space-y-1">
                            <label className="text-sm font-medium text-gray-700 block">{f.label}{f.required && <span className="text-red-600 ml-1">*</span>}</label>
                            {f.type === 'textarea' ? (
                              <textarea
                                value={waiverFieldValues[f.key] || ''}
                                onChange={(e) => setWaiverFieldValues({ ...waiverFieldValues, [f.key]: e.target.value })}
                                placeholder={f.placeholder || ''}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent text-sm"
                                data-testid={`waiver-field-${waiverModal.type}-${f.key}`}
                              />
                            ) : f.type === 'select' ? (
                              <select
                                value={waiverFieldValues[f.key] || ''}
                                onChange={(e) => setWaiverFieldValues({ ...waiverFieldValues, [f.key]: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent text-sm bg-white"
                                data-testid={`waiver-field-${waiverModal.type}-${f.key}`}
                              >
                                <option value="">Select…</option>
                                {(f.options || []).map(opt => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            ) : f.type === 'radio' ? (
                              <div className="space-y-1.5" data-testid={`waiver-field-${waiverModal.type}-${f.key}`}>
                                {(f.options || []).map(opt => (
                                  <label key={opt.value} className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                                    <input
                                      type="radio"
                                      name={`waiver-${waiverModal.type}-${f.key}`}
                                      value={opt.value}
                                      checked={waiverFieldValues[f.key] === opt.value}
                                      onChange={() => setWaiverFieldValues({ ...waiverFieldValues, [f.key]: opt.value })}
                                      className="mt-0.5 text-navy focus:ring-navy"
                                      data-testid={`waiver-field-${waiverModal.type}-${f.key}-${opt.value}`}
                                    />
                                    <span>{opt.label}</span>
                                  </label>
                                ))}
                              </div>
                            ) : f.type === 'date' ? (
                              <input
                                type="date"
                                value={waiverFieldValues[f.key] || ''}
                                onChange={(e) => setWaiverFieldValues({ ...waiverFieldValues, [f.key]: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent text-sm"
                                data-testid={`waiver-field-${waiverModal.type}-${f.key}`}
                              />
                            ) : (
                              <input
                                type={f.type === 'tel' ? 'tel' : 'text'}
                                value={waiverFieldValues[f.key] || ''}
                                onChange={(e) => setWaiverFieldValues({ ...waiverFieldValues, [f.key]: e.target.value })}
                                placeholder={f.placeholder || ''}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent text-sm"
                                data-testid={`waiver-field-${waiverModal.type}-${f.key}`}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Acknowledgment block — single checkbox covering all certification statements */}
                  {(() => {
                    const statements = WAIVER_ACK_STATEMENTS[waiverModal.type] || [];
                    return (
                      <div className={`rounded-lg border-2 p-3 transition-colors ${waiverAcknowledged ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={waiverAcknowledged}
                            onChange={(e) => setWaiverAcknowledged(e.target.checked)}
                            className="w-5 h-5 mt-0.5 rounded border-gray-300 text-navy focus:ring-navy flex-shrink-0"
                            data-testid="waiver-acknowledge-checkbox"
                          />
                          <span className="text-sm font-medium text-navy">
                            I certify and agree to all of the following:
                          </span>
                        </label>
                        {statements.length > 0 && (
                          <ul className="list-disc ml-10 mt-2 space-y-1 text-xs text-gray-700">
                            {statements.map((s, i) => (
                              <li key={i}>{s}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })()}

                  {/* Signature input */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 block">Type your full legal name to sign <span className="text-red-600">*</span></label>
                    <input
                      type="text"
                      value={waiverSignature}
                      onChange={(e) => setWaiverSignature(e.target.value)}
                      placeholder="Full Legal Name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent text-lg"
                      data-testid="waiver-signature-input"
                    />
                  </div>
                </div>

                {/* Sign button (sticky footer) */}
                <div className="p-4 border-t border-gray-200 bg-white">
                  <Button
                    className="w-full bg-red hover:bg-red/90 text-white h-12 font-bold"
                    disabled={!waiverFieldsComplete(waiverModal.type, waiverFieldValues) || !waiverAcknowledged || !waiverSignature.trim()}
                    onClick={handleSignWaiver}
                    data-testid="waiver-sign-button"
                  >
                    <Check className="w-5 h-5 mr-2" />
                    Sign &amp; Accept
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    // PAYMENT STEP
    if (step.id === 'payment') {
      const paymentStatus = registration?.payment_status || 'not_started';
      const paymentBadge = getPaymentStatusBadge(paymentStatus);
      const PaymentIcon = paymentBadge.icon;
      const amount = getPaymentAmount();
      // Balance-aware header: when the registration has a real positive
      // remaining balance and is not in a terminal/awaiting state, show
      // "BALANCE DUE $X.XX" plus a "$paid of $total paid" subline. For
      // confirmed/paid/waived/submitted, keep the existing "REGISTRATION
      // FEE" label so the conditional message blocks below stay
      // semantically aligned with the header.
      const amountPaid = Number(registration?.amount_paid || 0);
      const remaining = Math.max(0, amount - amountPaid);
      const balanceModeHidden = ['confirmed', 'paid', 'waived', 'submitted'].includes(paymentStatus);
      const showBalanceMode = remaining > 0 && !balanceModeHidden;

      return (
        <div className="space-y-6" data-testid="payment-step">
          {/* Payment Summary Header */}
          <div className="bg-navy text-white rounded-xl p-6 relative overflow-hidden" data-testid="payment-step-header">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gold/10 rounded-full -translate-y-12 translate-x-12"></div>
            <div className="relative flex items-center justify-between">
              <div>
                <p
                  className="text-xs font-bold uppercase tracking-wider text-gold mb-1"
                  data-testid="payment-header-label"
                >
                  {showBalanceMode ? 'Balance Due' : 'Registration Fee'}
                </p>
                <p
                  className="font-display text-4xl font-black"
                  data-testid="payment-header-amount"
                >
                  ${(showBalanceMode ? remaining : amount).toFixed(2)}
                </p>
                {showBalanceMode && amountPaid > 0 && (
                  <p
                    className="text-xs text-white/70 mt-1"
                    data-testid="payment-header-paid-subline"
                  >
                    ${amountPaid.toFixed(2)} of ${amount.toFixed(2)} paid
                  </p>
                )}
              </div>
              <div className="text-right">
                {selectedProgram && (
                  <p className="text-sm text-white/70 font-body">{selectedProgram.name}</p>
                )}
                <p className="text-sm text-white/70 font-body">{registration?.player?.first_name} {registration?.player?.last_name}</p>
              </div>
            </div>
          </div>

          {/* Payment Status */}
          <div className="p-4 rounded-lg border flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <PaymentIcon className={`w-6 h-6 ${paymentStatus === 'confirmed' || paymentStatus === 'waived' ? 'text-green-600' : 'text-gray-500'}`} />
              <div>
                <p className="font-medium">Payment Status</p>
                <Badge className={paymentBadge.className}>{paymentBadge.label}</Badge>
              </div>
            </div>
            {registration?.payment_method && (
              <p className="text-sm text-gray-600">
                Method: {PAYMENT_METHODS[registration.payment_method]?.name || registration.payment_method}
              </p>
            )}
          </div>

          {/* If payment is confirmed or waived, show success message */}
          {['confirmed', 'waived'].includes(paymentStatus) ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-green-800">Payment Complete!</h3>
              <p className="text-green-700 mt-1">
                {paymentStatus === 'waived' 
                  ? 'Payment has been waived for this registration.'
                  : 'Your payment has been confirmed. You can now submit your registration.'}
              </p>
            </div>
          ) : paymentStatus === 'submitted' ? (
            /* Payment submitted, awaiting confirmation */
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
              <Clock className="w-12 h-12 text-blue-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-blue-800">Payment Submitted</h3>
              <p className="text-blue-700 mt-1">
                Your payment has been submitted via {PAYMENT_METHODS[registration?.payment_method]?.name}. 
                Please wait for league confirmation before completing registration.
              </p>
              <p className="text-sm text-blue-600 mt-3">
                Manual payment methods may take 1-2 business days to confirm.
              </p>
            </div>
          ) : (
            /* Show payment methods */
            <>
              <div className="text-center">
                <h3 className="text-lg font-semibold text-navy">Select Payment Method</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Choose how you would like to pay your registration fee
                </p>
              </div>

              {/* No Refund Policy — long form */}
              <div
                className="bg-yellow-50 border border-yellow-200 rounded-xl p-4"
                data-testid="no-refund-policy-notice"
              >
                <h4 className="font-semibold text-yellow-800 flex items-center mb-2">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  No Refund Policy
                </h4>
                <p className="text-yellow-800 text-sm leading-relaxed">
                  All registration and program payments are final and non-refundable
                  unless Park City Chiefs cancels the program or season. By submitting
                  payment, you acknowledge and agree to this policy.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Object.values(PAYMENT_METHODS).map((method) => {
                  const isClover = method.id === 'clover_card';
                  
                  return (
                    <button
                      key={method.id}
                      onClick={() => setSelectedPaymentMethod(method.id)}
                      disabled={paymentSubmitting}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        selectedPaymentMethod === method.id
                          ? 'border-navy bg-navy/5'
                          : 'border-gray-200 hover:border-navy/50'
                      }`}
                      data-testid={`payment-method-${method.id}`}
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{method.icon}</span>
                        <div>
                          <p className="font-semibold text-navy">{method.name}</p>
                          {method.handle && (
                            <p className="text-sm text-gray-600">{method.handle}</p>
                          )}
                          {isClover && (
                            <p className="text-xs text-green-600 font-medium mt-0.5">Secure card payment</p>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Payment Instructions */}
              {selectedPaymentMethod && selectedPaymentMethod !== 'clover_card' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <h4 className="font-semibold text-yellow-800 flex items-center mb-2">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    Payment Instructions
                  </h4>
                  <p className="text-yellow-700 text-sm mb-3">
                    {PAYMENT_METHODS[selectedPaymentMethod].instructions}
                  </p>
                  
                  {/* Payment Details Box */}
                  <div className="bg-white rounded-lg p-4 border border-yellow-200">
                    <div className="flex justify-between items-center mb-3">
                      <div>
                        <p className="text-sm text-gray-600">Amount to send:</p>
                        <p className="text-2xl font-bold text-navy">${amount.toFixed(2)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Send to:</p>
                        <p className="text-lg font-semibold text-navy">{PAYMENT_METHODS[selectedPaymentMethod].handle}</p>
                      </div>
                    </div>
                    
                    {/* Payment Memo */}
                    <div className="pt-3 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        Include this in the payment memo/notes:
                      </p>
                      <div className="bg-navy/5 rounded-lg p-3 font-mono text-sm">
                        <span className="font-bold text-navy">
                          {registration?.player?.first_name} {registration?.player?.last_name}{' '}
                          {parseLocalDate(registration?.player?.date_of_birth)?.toLocaleDateString('en-US', {
                            month: '2-digit',
                            day: '2-digit',
                            year: '2-digit'
                          }) ?? ''}{' '}
                          {(() => {
                            const programType = 
                              formData?.program_type?.program_type ||
                              registration?.registration_data?.program_type?.program_type ||
                              registration?.program_type;
                            
                            if (programType === 'flag_football') return 'Flag';
                            if (programType === 'tackle_football') return 'Tackle';
                            if (programType === 'cheer') return 'Cheer';
                            return '';
                          })()}
                        </span>
                      </div>
                    </div>

                    {/* Direct Payment Link Button */}
                    {PAYMENT_METHODS[selectedPaymentMethod].hasDirectLink && (
                      <div className="mt-4">
                        <a
                          href={PAYMENT_METHODS[selectedPaymentMethod].getPaymentLink(
                            amount.toFixed(2),
                            `${registration?.player?.first_name || ''} ${registration?.player?.last_name || ''} ${
                              parseLocalDate(registration?.player?.date_of_birth)?.toLocaleDateString('en-US', {
                                month: '2-digit', day: '2-digit', year: '2-digit'
                              }) ?? ''
                            } ${
                              (() => {
                                const programType = 
                                  formData?.program_type?.program_type ||
                                  registration?.registration_data?.program_type?.program_type ||
                                  registration?.program_type;
                                if (programType === 'flag_football') return 'Flag';
                                if (programType === 'tackle_football') return 'Tackle';
                                if (programType === 'cheer') return 'Cheer';
                                return '';
                              })()
                            }`.trim(),
                            (() => {
                              const programType =
                                formData?.program_type?.program_type ||
                                registration?.registration_data?.program_type?.program_type ||
                                registration?.program_type;
                              if (programType === 'flag_football') return 'Flag';
                              if (programType === 'tackle_football') return 'Tackle';
                              if (programType === 'cheer') return 'Cheer';
                              return '';
                            })()
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full flex items-center justify-center px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
                          data-testid={`open-${selectedPaymentMethod}-btn`}
                        >
                          <ExternalLink className="w-5 h-5 mr-2" />
                          Open {PAYMENT_METHODS[selectedPaymentMethod].name} to Pay ${amount.toFixed(2)}
                        </a>
                        <p className="text-xs text-gray-500 text-center mt-2">
                          Opens in a new tab. Complete payment there, then return here.
                        </p>
                      </div>
                    )}

                    {/* Zelle - No direct link */}
                    {selectedPaymentMethod === 'zelle' && (
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-800 font-medium mb-1">Zelle Payment Steps:</p>
                        <ol className="text-sm text-blue-700 list-decimal list-inside space-y-1">
                          <li>Open your banking app (Chase, Bank of America, etc.)</li>
                          <li>Go to "Send Money with Zelle"</li>
                          <li>Enter: <span className="font-mono font-bold">takeoverelitesportsacademy@gmail.com</span></li>
                          <li>Enter amount: <span className="font-bold">${amount.toFixed(2)}</span></li>
                          <li>Add the player info in the memo</li>
                          <li>Send payment and return here</li>
                        </ol>
                      </div>
                    )}

                    {/* Shopify Pay Later — configuration notice if URL for this program is missing */}
                    {selectedPaymentMethod === 'shopify_payment_plan' && (() => {
                      const programType =
                        formData?.program_type?.program_type ||
                        registration?.registration_data?.program_type?.program_type ||
                        registration?.program_type;
                      const programUrl =
                        programType === 'flag_football' ? process.env.REACT_APP_SHOPIFY_PAYMENT_URL_FLAG
                        : programType === 'tackle_football' ? process.env.REACT_APP_SHOPIFY_PAYMENT_URL_TACKLE
                        : programType === 'cheer' ? process.env.REACT_APP_SHOPIFY_PAYMENT_URL_CHEER
                        : null;
                      if (programUrl) return null;
                      return (
                        <div
                          className="mt-4 p-3 bg-red/5 border border-red/20 rounded-lg"
                          data-testid="shopify-not-configured-notice"
                        >
                          <p className="text-sm text-red font-medium">
                            Shopify Checkout for this program is not yet configured.
                            Please contact the league to complete your Pay Later / Payment Plan payment.
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Clover Card Payment Form */}
              {selectedPaymentMethod === 'clover_card' && (
                <>
                  <p
                    className="text-xs text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-lg p-2 text-center"
                    data-testid="no-refund-policy-short-clover"
                  >
                    All payments are final. No refunds will be issued unless the program
                    is canceled by Park City Chiefs.
                  </p>
                  <CloverPaymentForm
                    amount={Math.round(amount * 100)}
                    description={`Registration: ${registration?.player?.first_name || ''} ${registration?.player?.last_name || ''}`}
                    paymentType="registration"
                    referenceId={registrationId}
                    onSuccess={async (result) => {
                      await fetchRegistration();
                    }}
                    onCancel={() => setSelectedPaymentMethod(null)}
                  />
                </>
              )}

              {/* Submit Payment Button (non-Clover methods only) */}
              {selectedPaymentMethod && selectedPaymentMethod !== 'clover_card' && (
                <>
                  <p
                    className="text-xs text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-lg p-2 text-center"
                    data-testid="no-refund-policy-short-manual"
                  >
                    All payments are final. No refunds will be issued unless the program
                    is canceled by Park City Chiefs.
                  </p>
                  <Button
                    onClick={() => handleSubmitPayment(selectedPaymentMethod)}
                    disabled={paymentSubmitting}
                    className="w-full bg-navy hover:bg-navy/90 text-white py-3"
                    data-testid="submit-payment-btn"
                  >
                    {paymentSubmitting ? (
                      'Submitting...'
                    ) : (
                      <>
                        <DollarSign className="w-5 h-5 mr-2" />
                        I've Sent Payment via {PAYMENT_METHODS[selectedPaymentMethod].name}
                      </>
                    )}
                  </Button>
                </>
              )}

              <p className="text-xs text-gray-500 text-center">
                After submitting, your payment will need to be confirmed by the league before you can complete registration.
              </p>
            </>
          )}
        </div>
      );
    }

    // REVIEW STEP
    if (step.id === 'review') {
      const paymentStatus = registration?.payment_status || 'not_started';
      const canSubmit = ['confirmed', 'waived'].includes(paymentStatus);
      
      return (
        <div className="space-y-4" data-testid="review-step">
          {canSubmit ? (
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <h3 className="font-semibold text-green-800 flex items-center">
                <CheckCircle className="w-5 h-5 mr-2" />
                Ready to Submit!
              </h3>
              <p className="text-sm text-green-700 mt-1">All requirements are complete. Review and submit your registration.</p>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <h3 className="font-semibold text-yellow-800 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2" />
                Payment Required
              </h3>
              <p className="text-sm text-yellow-700 mt-1">
                {paymentStatus === 'submitted' 
                  ? 'Your payment has been submitted and is awaiting confirmation. Registration cannot be finalized until payment is confirmed.'
                  : 'Payment must be confirmed before registration can be submitted.'}
              </p>
            </div>
          )}
          
          <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
            <p className="flex justify-between">
              <strong>Player:</strong> 
              <span>{registration?.player?.first_name} {registration?.player?.last_name}</span>
            </p>
            <p className="flex justify-between">
              <strong>Program:</strong>
              <span>{selectedProgram?.name || 'Not selected'}</span>
            </p>
            {/* Division with full display name */}
            <p className="flex justify-between">
              <strong>Division:</strong>
              <span>{formData?.program_type?.division_name || registration?.division_name || registration?.player?.age_group || 'Not assigned'}</span>
            </p>
            <p className="flex justify-between">
              <strong>Fee:</strong>
              <span>${getPaymentAmount().toFixed(2)}</span>
            </p>
            <p className="flex justify-between">
              <strong>Payment Status:</strong>
              <Badge className={getPaymentStatusBadge(paymentStatus).className}>
                {getPaymentStatusBadge(paymentStatus).label}
              </Badge>
            </p>
            <p className="flex justify-between">
              <strong>Documents:</strong>
              <span>{registration?.documents?.length || 0} uploaded</span>
            </p>
            <p className="flex justify-between">
              <strong>Waivers:</strong>
              <span>{registration?.waivers?.filter(w => w.status === 'signed').length || 0} of 3 signed</span>
            </p>
          </div>
          
          {!canSubmit && (
            <p className="text-sm text-gray-500 text-center">
              Registration is not complete until payment is confirmed.
            </p>
          )}
          
          {/* Follow Our Socials — optional, non-blocking */}
          <div className="bg-navy/5 border border-navy/10 rounded-xl p-5" data-testid="follow-socials-card">
            <div className="text-center mb-3">
              <p className="font-display text-base font-black text-navy uppercase tracking-tight">Stay Connected</p>
              <p className="text-xs text-gray-500 mt-0.5">Follow us to get the latest updates, game highlights, and announcements</p>
            </div>
            <div className="flex items-center justify-center gap-3">
              <a
                href="https://www.facebook.com/profile.php?id=61574361676415"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2.5 bg-[#1877F2] text-white rounded-lg text-xs font-bold uppercase tracking-wider hover:opacity-90 transition-opacity"
                data-testid="follow-facebook"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                Facebook
              </a>
              <a
                href="https://www.instagram.com/parkcitychiefs/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-br from-[#f09433] via-[#e6683c] to-[#bc1888] text-white rounded-lg text-xs font-bold uppercase tracking-wider hover:opacity-90 transition-opacity"
                data-testid="follow-instagram"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                Instagram
              </a>
              <a
                href="https://www.tiktok.com/@park.city.chiefs"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2.5 bg-black text-white rounded-lg text-xs font-bold uppercase tracking-wider hover:opacity-90 transition-opacity"
                data-testid="follow-tiktok"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                TikTok
              </a>
            </div>
          </div>
        </div>
      );
    }

    return <div>Step content</div>;
  };

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </ParentLayout>
    );
  }

  const paymentStatus = registration?.payment_status || 'not_started';
  const canSubmitRegistration = ['confirmed', 'waived'].includes(paymentStatus);

  return (
    <ParentLayout>
      <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8" data-testid="registration-wizard">
        {/* Header with Program Badge - Athletic Style */}
        <div className="mb-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-2">Fall 2026 Season</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-navy uppercase tracking-tighter mb-2">Player Registration</h1>
          <div className="flex items-center space-x-3">
            <p className="text-gray-600 font-body">{registration?.player?.first_name} {registration?.player?.last_name}</p>
            {selectedProgram && (
              <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1 rounded ${selectedProgram.color} text-white`}>
                {selectedProgram.name}
              </span>
            )}
          </div>
        </div>

        {/* Steps Progress - Athletic Yard Line Style */}
        <div className="mb-8 overflow-x-auto pb-4">
          <div className="bg-gray-100 rounded-xl p-3">
            <div className="flex items-center justify-between min-w-max gap-1">
              {STEPS.map((step, index) => {
                const isCompleted = registration?.steps_completed?.includes(step.id);
                const isCurrent = index === currentStep;
                return (
                  <div key={step.id} className="flex items-center flex-1">
                    <button
                      onClick={() => setCurrentStep(index)}
                      className={`flex flex-col items-center w-full p-2 rounded-lg transition-all duration-200 ${
                        isCurrent ? 'bg-navy shadow-md' : isCompleted ? 'bg-green-500/10 hover:bg-green-500/20' : 'hover:bg-gray-200'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                        isCompleted ? 'bg-green-500 border-green-500 text-white' :
                        isCurrent ? 'bg-white border-white text-navy' :
                        'bg-white border-gray-300 text-gray-400'
                      }`}>
                        {isCompleted ? <Check className="w-4 h-4" /> : <span className="font-display text-sm font-black">{index + 1}</span>}
                      </div>
                      <p className={`text-[10px] font-bold uppercase tracking-wider mt-1.5 ${
                        isCurrent ? 'text-white' : isCompleted ? 'text-green-700' : 'text-gray-500'
                      }`}>{step.name}</p>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Step Content Card - Athletic Style */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6 hover:shadow-lg transition-all duration-200">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
            <h2 className="font-display text-xl font-black text-navy uppercase tracking-tight">{STEPS[currentStep].name}</h2>
            <p className="text-sm text-gray-600 font-body mt-1">{STEPS[currentStep].description}</p>
          </div>
          <div className="p-5 sm:p-6">{renderStepContent()}</div>
        </div>

        {/* Navigation - Athletic Buttons */}
        <div className="flex justify-between gap-4">
          <Button 
            onClick={handleBack} 
            variant="outline" 
            disabled={currentStep === 0}
            className="border-2 border-gray-300 hover:border-navy hover:-translate-y-0.5 transition-all duration-200 font-bold uppercase tracking-wider"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />Back
          </Button>
          {currentStep === STEPS.length - 1 ? (
            <Button 
              onClick={handleSubmit} 
              disabled={saving || !canSubmitRegistration} 
              className="bg-green-600 hover:bg-green-700 hover:-translate-y-0.5 hover:shadow-lg text-white font-bold uppercase tracking-wider transition-all duration-200"
              data-testid="submit-registration-btn"
            >
              {saving ? 'Submitting...' : 'Submit Registration'}
            </Button>
          ) : (
            <Button 
              onClick={handleNext} 
              disabled={saving} 
              className="bg-red hover:bg-red/90 hover:-translate-y-0.5 hover:shadow-lg text-white font-bold uppercase tracking-wider transition-all duration-200"
            >
              {saving ? 'Saving...' : 'Next'}<ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </ParentLayout>
  );
};

export default RegistrationWizard;
